
/**
 * Checks if an object is a plain object.
 * 
 * @param {Object} obj The object to check.
 * @returns {Boolean}
 */
export function isPlainObject(obj) {
    return typeof obj === 'object'
        && obj !== null
        && Object.prototype.toString.call(obj) === '[object Object]';
}

/**
 * Deep clones an object.
 * 
 * @param {Object} obj The object to clone.
 * @returns {Object} The cloned object.
 */
export function cloneObject(obj) {
    var clone = Array.isArray(obj) ? [] : {};

    for (var k in obj) {
        if (obj[k] != null && (isPlainObject(obj[k]) || Array.isArray(obj[k]))) {
            clone[k] = cloneObject(obj[k]);
        } else {
            clone[k] = obj[k];
        }
    }

    return clone;
}

/**
 * Flattens an object.
 * eg: { my: { flattend: { key: 'hello' } } } results in { 'my.flattend.key' => 'hello' }.
 * 
 * @author grimzy https://github.com/kazupon/vue-i18n/issues/584
 * 
 * @param {Object} obj The object to flatten.
 * @param {String} prefix A prefix to use for the keys.
 * @param {String} glue The glue to use for concatinating the keys.
 * @returns {Object} The flattend object.
 */
export function flattenObject(obj, prefix = '', glue = '.') {
    return Object.keys(obj).reduce((params, key) => {
        const pre = prefix.length ? prefix + glue : '';

        if (isPlainObject(obj[key])) {
            Object.assign(params, flattenObject(obj[key], pre + key));
        } else {
            params[pre + key] = obj[key];
        }

        return params;
    }, {});
}

/**
 * Unflattens an object.
 * eg: { 'my.flattend.key' => 'hello' } results in { my: { flattend: { key: 'hello' } } }
 * 
 * @param {Object} obj The object with the flattend keys.
 * @param {String} glue The glue used in the flattend keys.
 * @param {Boolean} useArrays If it should convert to arrays if all keys are numeric.
 * @returns {Object} The unflattened object
 */
export function unflattenObject(obj, glue = '.', useArrays = false) {
    let paths = Object.keys(obj).map(key => { return { pathParts: key.split(glue), val: obj[key] }; });

    let unflattendedObject = paths.reduce((obj, path) => {
        let target = obj;


        for (let pathPart of path.pathParts) {
            if (!(pathPart in target)) {
                target[pathPart] = {};
            }
            target = target[pathPart];
        }

        return obj;
    }, {});

    paths.forEach(path => {
        let target = unflattendedObject;
        let lastPathPart = path.pathParts.pop();

        for (let pathPart of path.pathParts) {
            if (useArrays && Object.keys(target[pathPart]).every(k => !isNaN(k))) {
                target[pathPart] = Object.values(target[pathPart]);
            }
            target = target[pathPart];
        }

        target[lastPathPart] = path.val;
    });

    return unflattendedObject;
}

/**
 * Deep merge two objects.
 * Arrays will get concatinated by default.
 * It is also possible to merge arrays of objects by key-matching.
 * 
 * @param {Object} obj1 First object.
 * @param {Object} obj2 Second object.
 * @param {Boolean} concatArrays If arrays should get concatinated.
 * @param {Array} arraysCheckKeys Keys to use for key-matching when merging two arrays of objects (only if concatArrays is disabled).
 * @param {Function} set A function used to set a value on the target object. Gets the arguments 'target', 'key', 'val' and has to set the given value on the given key of the target.
 * @returns {Object} A new object with the merged data.
 */
export function mergeDeep(obj1, obj2, concatArrays = true, arraysCheckKeys = null, set = null) {
    const objects = [obj1, obj2];
    set = set != null ? set : function (target, key, val) {
        target[key] = val;
    };

    return objects.reduce((prev, obj) => {
        Object.keys(obj).forEach(key => {
            const pVal = prev[key];
            const oVal = obj[key];
            return reduceCheck(prev, key, pVal, oVal, concatArrays, arraysCheckKeys, set);
        });
        return prev;
    }, {});
}

function reduceCheck(prev, key, pVal, oVal, concatArrays = true, arraysCheckKeys = null, set = null) {
    if (Array.isArray(pVal) && Array.isArray(oVal)) {
        if (concatArrays) {
            prev[key] = pVal.concat(...oVal);
        } else {
            for (let i = 0; i < oVal.length; i++) {
                let targetIndex = i;

                if (arraysCheckKeys && typeof oVal[i] == 'object') {
                    const keyVals = {};
                    for (const key of arraysCheckKeys) {
                        if (key in oVal[i]) {
                            keyVals[key] = oVal[i][key];
                        }
                    }

                    const checkKeys = Object.keys(keyVals);

                    if (checkKeys.length > 0) {
                        for (let j = 0; j < pVal.length; j++) {
                            if (typeof pVal[j] == 'object' && checkKeys.every(key => {
                                return key in pVal[j] && pVal[j][key] == keyVals[key];
                            })) {
                                targetIndex = j;
                                break;
                            }
                        }
                    }
                }

                if (targetIndex in pVal) {
                    const merged = mergeDeep({ val: pVal[targetIndex] }, { val: oVal[i] }, false, arraysCheckKeys, set);
                    set(pVal, targetIndex, merged.val)
                } else {
                    pVal.push(oVal[i]);
                }
            }

            set(prev, key, pVal);
        }
    }
    else if (isPlainObject(pVal) && isPlainObject(oVal)) {
        set(prev, key, mergeDeep(pVal, oVal, concatArrays, arraysCheckKeys, set));
    }
    else {
        set(prev, key, oVal);
    }
}

/**
 * Reduces an array of objects into a single object.
 * eg: [ { prop1: 1 }, { prop2: 2 } ] will result in { prop1: 1, prop2: 2 }.
 * 
 * @param {Array} arr The array of objects.
 * @returns {Object} The reduced object.
 */
export function reduceArrayObjects(arr) {
    return arr.reduce((carry, item) => {
        Object.keys(item).forEach(key => {
            carry[key] = item[key];
        });

        return carry;
    }, {});
}

/**
 * Reduces arrays of objects inside an object into single objects.
 * eg: Object { arr1: [ { prop1: 1 }, { prop2: 2 } ] } with keys [ 'arr1' ] will result in { arr1: { prop1: 1, prop2: 2 } }
 * 
 * @param {Object} obj The object which holds the arrays to reduce.
 * @param {Array} keys The keys of the arrays to reduce within the object.
 * @returns {Object} The given object with the arrays reduced.
 */
export function reduceArrayObjectsMulti(obj, keys) {
    for (var key of keys) {
        obj[key] = reduceArrayObjects(obj[key]);
    }

    return obj;
}

/**
 * Get the value of a given path in the properties of an object.
 * eg: Using the path 'my.path' on the object { my: { path: 'hello' } } would return the value 'hello'.
 * 
 * @param {Object} obj The object to search in
 * @param {String} path The path to search
 * @param {String} pathSeperator The path seperator
 * @returns {*} The found value or undefined
 */
export function findByPath(obj, path, pathSeperator = '.') {
    path = path.split(pathSeperator);

    let current = obj;

    for (const p of path) {
        if (p in current) {
            current = current[p];
        } else {
            return undefined;
        }
    }

    return current;
}

/**
 * Convert to a Date object.
 * 
 * @param {(Object|String)} dateObject The value to convert - a string or an object with 'date' and optional 'time' property.
 * @returns {(Object|null)}
 */
export function toDate(dateObject) {
    if (dateObject instanceof Date) {
        return dateObject;
    }

    if (typeof dateObject == 'string') {
        return new Date(dateObject);
    }

    if (dateObject && typeof dateObject == 'object' && 'date' in dateObject) {
        let date = dateObject['date'] instanceof Date ? dateObject['date'] : new Date(dateObject['date']);

        if ('time' in dateObject) {
            let time = dateObject['time'].split(':');

            if (time.length == 2) {
                date.setHours(time[0]);
                date.setMinutes(time[1]);
            }
        }

        return date;
    }

    return null;
}

/**
 * Checks if an object is a fetch response
 * 
 * @param {Object} obj The object to check
 * @returns {Boolean}
 */
export function isFetchResponse(obj) {
    return obj
        && (
            obj instanceof Response
            || ( // "instanceof Response" does not work on ios safari (neither does checking the __proto__ property), so we work around this by checking some properties a fetch Response would have..
                typeof obj == 'object'
                && 'status' in obj
                && 'ok' in obj
                && 'json' in obj
                && typeof obj.json == 'function'
                && 'blob' in obj
                && typeof obj.blob == 'function'
                && 'text' in obj
                && typeof obj.text == 'function'
            )
        );
}

/**
 * Get the value for an argument by type. 
 * Uses alternative arguments as fallback if the actual argument value is not of this type or is set to the given ignoreValue.
 * 
 * @param {*} argValue The original value of the argument.
 * @param {String} type The type the arg value should have.
 * @param {(Object|Array)} args The alternative argument values.
 * @param {*} ignoreValue The default value which should be ignored and returend if no value of the given type has been found.
 * @returns {*}
 */
export function getArgByType(argValue, type, args = [], ignoreValue = null) {
    if (argValue != ignoreValue) {
        return argValue;
    }

    const argsValues = Object.values(args);

    for (const val of argsValues) {
        if (val != ignoreValue && typeof val == type) {
            return val;
        }
    }

    return ignoreValue;
}